import { Component, ReactNode } from "react";
import { WithTranslation, withTranslation } from "react-i18next";

import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import { Box, Button, Container, Divider, Typography } from "@mui/material";

import { Body } from "@/components/layout";

import { Takeaway } from "./Takeaway";

interface ErrorBoundaryProps extends WithTranslation {
  children: ReactNode;
  fallbackTitle?: string;
  fallbackMessage?: string;
  onReset?: () => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error Boundary component to catch and handle React component errors gracefully
 * Prevents the entire app from crashing when a component error occurs
 */
class ErrorBoundaryComponent extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error to console for debugging
    console.error("[ErrorBoundary] Caught error:", error);
    console.error("[ErrorBoundary] Error info:", errorInfo);

    // Check if this is a chunk loading error (stale build after deployment)
    const isChunkLoadError =
      error.name === "ChunkLoadError" ||
      error.message.includes("Failed to fetch dynamically imported module") ||
      error.message.includes("Importing a module script failed");

    if (isChunkLoadError) {
      console.warn(
        "[ErrorBoundary] Detected chunk load error - likely due to deployment. Reloading page...",
      );
      // Reload the page to get the latest version
      window.location.reload();
      return;
    }

    // In production, you could also send this to an error tracking service
    // e.g., Sentry, LogRocket, etc.
  }

  handleReset = () => {
    // Reset error state
    this.setState({
      hasError: false,
      error: null,
    });

    // Call custom reset handler if provided
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  render() {
    if (this.state.hasError) {
      const { t } = this.props;
      const {
        fallbackTitle = t("errors.somethingWentWrong"),
        fallbackMessage = t("errors.unexpectedError"),
      } = this.props;

      return (
        <Body>
          <Container
            maxWidth="md"
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              minHeight: "100%",
            }}
          >
            <Takeaway
              overtitle={"Whoops!"}
              title={fallbackTitle}
              text={fallbackMessage}
              action={
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<RefreshRoundedIcon />}
                  onClick={this.handleReset}
                >
                  {t("common.tryAgain")}
                </Button>
              }
            >
              {import.meta.env.DEV && this.state.error && (
                <>
                  <Divider />
                  <Typography
                    variant="caption"
                    component="div"
                    sx={{ fontWeight: 600, mb: 1 }}
                  >
                    {t("errors.errorDetailsDevMode")}
                  </Typography>
                  <Typography variant="caption" component="pre">
                    {this.state.error.message}
                    {"\n"}
                    {this.state.error.stack}
                  </Typography>
                </>
              )}
            </Takeaway>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "60vh",
                textAlign: "center",
                gap: 3,
              }}
            ></Box>
          </Container>
        </Body>
      );
    }

    return this.props.children;
  }
}

export const ErrorBoundary = withTranslation()(ErrorBoundaryComponent);
